
import { addDays } from "date-fns";
import Enumerable from "linq";
import { ColumnType, DataSourceType, DateRangeType, DurationTimeframeTypeEnum, OptionalColumnEnum, SortOrder, Style } from "../../enums/DRCXEnums";
import { eLynxObjectTypeEnum } from "../../enums/eLynxObjectType";
import { parseColumnEquatableKey } from "../../utilities/drcx/ParseColumnEquatableKey";
import UUID from "../../utilities/Uuid";
import { DRCXConfigurationSectionColumnModel } from "./DRCXConfigurationSectionColumnModel";
import { IDRCXConfigurationSectionDTOBase } from "./DRCXConfigurationSectionDTOBase";
import { DRCXConfigurationSectionObjectModel } from "./DRCXConfigurationSectionObjectModel";

export abstract class DRCXConfigurationSectionModelBase { //implements IEquatable<string> {
    // Properties
    public id: number = 0;
    public name: string = "New Section";
    public order: number = 0;
    public style: Style = Style.Pivot_Object_Timeframe;
    public componentName: string | undefined = undefined;
    public procedureName: string | undefined = undefined;
    public created: Date = new Date();
    public dataSource: DataSourceType = DataSourceType.CurrentData;
    public useTatEffectiveDate: boolean = false;
    public varianceIgnoreEffectiveDayOffset: boolean = false;
    public noAlarmDataForAggregates: boolean = false;
    public includeDaysWithNoData: boolean = false;
    public includeDevicesWithNoData: boolean = false;
    public includeDevicesWithNoTags: boolean = false;
    public includeTanks: boolean = false;
    public includeTotals: boolean = false;
    public dateSortOrder: SortOrder = SortOrder.Ascending;
    public deviceSortOrder: SortOrder = SortOrder.Ascending;
    public deviceSortColumn: OptionalColumnEnum = OptionalColumnEnum.DeviceDisplayName;

    public durationType: DurationTimeframeTypeEnum | undefined = DurationTimeframeTypeEnum.MultipleTimeFrames;

    // durationType == SpecificStartOrStop
    public fixedStartDate: Date | undefined = addDays(new Date(), -7);
    public fixedStartDateRangeType: DateRangeType | undefined = DateRangeType.Raw;
    public fixedEndDate: Date | undefined = new Date();
    public fixedEndDateRangeType: DateRangeType | undefined = DateRangeType.Raw;

    // durationType == MultipleTimeFrames
    public multipleStartOffset: number | undefined = -6;
    public multipleStartRangeType: DateRangeType | undefined = DateRangeType.Day;
    public multipleDuration: number | undefined = 7;
    public multipleDurationRangeType: DateRangeType | undefined = DateRangeType.Day;

    // durationType == CurrentSingleTimeFrame
    public currentRangeType: DateRangeType | undefined = DateRangeType.Month;

    // durationType == PreviousSingleTimeFrame
    public previousRangeType: DateRangeType | undefined = DateRangeType.Hour;

    public runToCurrentDate: boolean | undefined = false;
    public currentDateOffset: number | undefined = 0;
    public currentDateRangeType: DateRangeType | undefined = undefined;
    public retrievalStartOffset: number = 0;
    public retrievalEndOffset: number = 0;

    public consumerHandlesSorting: boolean = false;
    public includeDeviceCommStatus: boolean = false;
    public includeTagLKVAlarmStatus: boolean = false;
    public getAlarmData: boolean = false;
    public includeTagLKVStaleDataStatus: boolean = false;
    public lockedColumnCount: number = 0;
    public checkAccessControl: boolean = true;
    public displayDeviceMemos: boolean = false;
    public displaySettingsChanges: boolean = false;
    public displayTagMemos: boolean = false;
    public rawDataClosenessMin?: number = undefined;
    public rawDataClosenessSec?: number = undefined;

    // Collections
    public objects: Array<DRCXConfigurationSectionObjectModel> = new Array<DRCXConfigurationSectionObjectModel>();
    public columns: Array<DRCXConfigurationSectionColumnModel> = new Array<DRCXConfigurationSectionColumnModel>();

    // UI Only
    // public isExpanded: boolean = true;
    // public isMenuVisible: boolean = false;
    // public scrollIntoView: boolean = false;
    // public isValid: boolean = false;
    //public isRawDataCloseness: boolean = false;
    // TODO: Some of those above are not needed
    private _equatableKey: string = UUID.generate();

    constructor(dto?: IDRCXConfigurationSectionDTOBase) {
        if (dto) {
            if (typeof dto.id !== "undefined") {
                this.id = dto.id;
            }
            if (typeof dto.name !== "undefined") {
                this.name = dto.name;
            }
            if (typeof dto.order !== "undefined") {
                this.order = dto.order;
            }
            if (typeof dto.style !== "undefined") {
                this.style = dto.style;
            }
            if (typeof dto.componentName !== "undefined") {
                this.componentName = dto.componentName;
            }
            if (typeof dto.procedureName !== "undefined") {
                this.procedureName = dto.procedureName;
            }
            if (typeof dto.created !== "undefined") {
                this.created = new Date(dto.created);
            }
            if (typeof dto.dataSource !== "undefined") {
                this.dataSource = dto.dataSource;
            }
            if (typeof dto.useTatEffectiveDate !== "undefined") {
                this.useTatEffectiveDate = dto.useTatEffectiveDate;
            }
            if (typeof dto.varianceIgnoreEffectiveDayOffset !== "undefined") {
                this.varianceIgnoreEffectiveDayOffset = dto.varianceIgnoreEffectiveDayOffset;
            }
            if (typeof dto.noAlarmDataForAggregates !== "undefined") {
                this.noAlarmDataForAggregates = dto.noAlarmDataForAggregates;
            }
            if (typeof dto.includeDaysWithNoData !== "undefined") {
                this.includeDaysWithNoData = dto.includeDaysWithNoData;
            }
            if (typeof dto.includeDevicesWithNoData !== "undefined") {
                this.includeDevicesWithNoData = dto.includeDevicesWithNoData;
            }
            if (typeof dto.includeDevicesWithNoTags !== "undefined") {
                this.includeDevicesWithNoTags = dto.includeDevicesWithNoTags;
            }
            if (typeof dto.includeTanks !== "undefined") {
                this.includeTanks = dto.includeTanks;
            }
            if (typeof dto.includeTotals !== "undefined") {
                this.includeTotals = dto.includeTotals;
            }
            if (typeof dto.dateSortOrder !== "undefined") {
                this.dateSortOrder = dto.dateSortOrder;
            }
            if (typeof dto.deviceSortOrder !== "undefined") {
                this.deviceSortOrder = dto.deviceSortOrder;
            }
            if (typeof dto.deviceSortColumn !== "undefined") {
                this.deviceSortColumn = dto.deviceSortColumn;
            }
            if (typeof dto.timeframe !== "undefined" && dto.timeframe) {
                this.durationType = dto.timeframe.durationTimeframeType;
                if (dto.timeframe.durationTimeframeType ===
                    DurationTimeframeTypeEnum.MultipleTimeFrames) {
                    if (typeof dto.timeframe.calculatedStartDateOffset !== "undefined") {
                        this.multipleStartOffset = dto.timeframe.calculatedStartDateOffset
                            ? dto.timeframe.calculatedStartDateOffset.duration
                            : undefined;
                        this.multipleStartRangeType = dto.timeframe.calculatedStartDateOffset
                            ? dto.timeframe.calculatedStartDateOffset.rangeType
                            : undefined;
                    }
                    if (typeof dto.timeframe.duration !== "undefined") {
                        this.multipleDuration = dto.timeframe.duration
                            ? dto.timeframe.duration.duration
                            : undefined;
                        this.multipleDurationRangeType = dto.timeframe.duration
                            ? dto.timeframe.duration.rangeType
                            : undefined;
                    }
                } else if (dto.timeframe.durationTimeframeType ===
                    DurationTimeframeTypeEnum.CurrentSingleTimeFrame) {
                    if (typeof dto.timeframe.duration !== "undefined") {
                        this.currentRangeType = dto.timeframe.duration
                            ? dto.timeframe.duration.rangeType
                            : undefined;
                    }
                } else if (dto.timeframe.durationTimeframeType ===
                    DurationTimeframeTypeEnum.PreviousSingleTimeFrame) {
                    if (typeof dto.timeframe.duration !== "undefined") {
                        this.previousRangeType = dto.timeframe.duration
                            ? dto.timeframe.duration.rangeType
                            : undefined;
                    }
                } else if (dto.timeframe.durationTimeframeType ===
                    DurationTimeframeTypeEnum.SpecificStartOrStop) {
                    if (typeof dto.timeframe.fixedStartDate !== "undefined") {
                        this.fixedStartDate = dto.timeframe.fixedStartDate
                            ? new Date(dto.timeframe.fixedStartDate.timestamp)
                            : undefined;
                        this.fixedStartDateRangeType = dto.timeframe.fixedStartDate
                            ? dto.timeframe.fixedStartDate.rangeType
                            : undefined;
                    }
                    if (typeof dto.timeframe.fixedEndDate !== "undefined") {
                        this.fixedEndDate = dto.timeframe.fixedEndDate
                            ? new Date(dto.timeframe.fixedEndDate.timestamp)
                            : undefined;
                        this.fixedEndDateRangeType = dto.timeframe.fixedEndDate
                            ? dto.timeframe.fixedEndDate.rangeType
                            : undefined;
                    }
                }
                this.runToCurrentDate = dto.timeframe.runToCurrentDate;
                if (typeof dto.timeframe.currentDateOffset !== "undefined") {
                    this.currentDateOffset = dto.timeframe.currentDateOffset
                        ? dto.timeframe.currentDateOffset.duration
                        : undefined;
                    this.currentDateRangeType = dto.timeframe.currentDateOffset
                        ? dto.timeframe.currentDateOffset.rangeType
                        : undefined;
                }
            }
        }
        if (typeof dto?.retrievalStartOffset !== "undefined") {
            this.retrievalStartOffset = dto.retrievalStartOffset;
        }
        if (typeof dto?.retrievalEndOffset !== "undefined") {
            this.retrievalEndOffset = dto.retrievalEndOffset;
        }
        if (typeof dto?.consumerHandlesSorting !== "undefined") {
            this.consumerHandlesSorting = dto.consumerHandlesSorting;
        }
        if (typeof dto?.includeDeviceCommStatus !== "undefined") {
            this.includeDeviceCommStatus = dto.includeDeviceCommStatus;
        }
        if (typeof dto?.includeTagLKVAlarmStatus !== "undefined") {
            this.includeTagLKVAlarmStatus = dto.includeTagLKVAlarmStatus;
        }
        if (typeof dto?.getAlarmData !== "undefined") {
            this.getAlarmData = dto.getAlarmData;
        }
        if (typeof dto?.includeTagLKVStaleDataStatus !== "undefined") {
            this.includeTagLKVStaleDataStatus = dto.includeTagLKVStaleDataStatus;
        }
        if (typeof dto?.lockedColumnCount !== "undefined") {
            this.lockedColumnCount = dto.lockedColumnCount;
        }
        if (typeof dto?.checkAccessControl !== "undefined") {
            this.checkAccessControl = dto.checkAccessControl;
        }
        if (typeof dto?.displayDeviceMemos !== "undefined") {
            this.displayDeviceMemos = dto.displayDeviceMemos;
        }
        if (typeof dto?.displaySettingsChanges !== "undefined") {
            this.displaySettingsChanges = dto.displaySettingsChanges;
        }
        if (typeof dto?.displayTagMemos !== "undefined") {
            this.displayTagMemos = dto.displayTagMemos;
        }

        if (typeof dto?.rawDataClosenessMin !== "undefined") {
            this.rawDataClosenessMin = dto.rawDataClosenessMin;
        }

        if (typeof dto?.rawDataClosenessSec !== "undefined") {
            this.rawDataClosenessSec = dto.rawDataClosenessSec;
        }

        if (typeof dto?.objects !== "undefined") {
            this.objects = dto.objects
                ? dto.objects.map(
                    object => new DRCXConfigurationSectionObjectModel(object)
                )
                : new Array<DRCXConfigurationSectionObjectModel>();
        }
        if (typeof dto?.columns !== "undefined") {
            this.columns = dto.columns
                ? dto.columns.map(
                    column => new DRCXConfigurationSectionColumnModel(column)
                )
                : new Array<DRCXConfigurationSectionColumnModel>();
        }
    }

    public toDTO(): IDRCXConfigurationSectionDTOBase {
        return this._toDTO({});
    }

    public toJSON(): any {
        return this.toDTO();
    }

    // public isSectionValid(): boolean {
    //     return this.isValid;
    // }

    // public abstract clone(): DRCXConfigurationSectionModelBase;

    // public abstract validate(): boolean;

    public abstract hasDeprecatedFeatures(): boolean;

    // public abstract addObject(name: string, equatableKey: string, deviceType?: DeviceTypesEnum, ruleType?: RulesEngineRuleTypeEnum): void;

    // public removeObject(equatableKey: string) {
    //     // Find the item
    //     const itemToRemove = Enumerable
    //         .from(this.objects)
    //         .where((obj: DRCXConfigurationSectionObjectModel) => obj.equatableKey === equatableKey)
    //         .firstOrDefault();

    //     // Did we find it?
    //     if (itemToRemove) {
    //         const index = this.objects.indexOf(itemToRemove);
    //         if (index > -1) {
    //             this.objects.splice(index, 1);

    //             // Reset the object orders to remove gaps
    //             for (let orderIdx = 0; orderIdx < this.objects.length; orderIdx++) {
    //                 this.objects[orderIdx].order = orderIdx;
    //             }
    //         }
    //     }
    // }

    // public clearObjects(): void {
    //     this.objects.length = 0;
    // }

    public addColumn(name: string,
        equatableKey: string,
        //isNew: boolean = false,
        isDefault: boolean = false,
        isRequired: boolean = false,
        //eventColumn: EventColumnEnum | undefined = undefined
    ): void {
        if (!Enumerable.from(this.columns)
            .any((col: DRCXConfigurationSectionColumnModel) => col.equatableKey === equatableKey)) {
            const column: DRCXConfigurationSectionColumnModel = new DRCXConfigurationSectionColumnModel({
                name: name,
                order: this.columns.length,
                isDefault: isDefault,
                isRequired: isRequired,
                //eventColumn: eventColumn,
                ...parseColumnEquatableKey(equatableKey)
            });
            //column.equatableKey = equatableKey;
            //column.isNew = isNew;

            this.columns.push(column);
        }
    }

    // public removeColumn(equatableKey: string) {
    //     // Find the item
    //     const itemToRemove = Enumerable
    //         .from(this.columns)
    //         .where((col: DRCXConfigurationSectionColumnModel) => col.equatableKey === equatableKey)
    //         .firstOrDefault();

    //     // Did we find it?
    //     if (itemToRemove) {
    //         const index = this.columns.indexOf(itemToRemove);
    //         if (index > -1) {
    //             this.columns.splice(index, 1);

    //             // Reset the column orders to remove gaps
    //             for (let orderIdx = 0; orderIdx < this.columns.length; orderIdx++) {
    //                 this.columns[orderIdx].order = orderIdx;
    //             }
    //         }
    //     }
    // }

    // public removeColumnsByTatID(tatID: number) {
    //     // Find the item
    //     const itemsToRemove = Enumerable
    //         .from(this.columns)
    //         .where((col: DRCXConfigurationSectionColumnModel) => col.tagAssociationTypeID === tatID)
    //         .toArray();

    //     // Did we find any?
    //     if (itemsToRemove && itemsToRemove.length > 0) {
    //         itemsToRemove.forEach(itemToRemove => {
    //             const index = this.columns.indexOf(itemToRemove);
    //             if (index > -1) {
    //                 this.columns.splice(index, 1);

    //                 // Reset the column orders to remove gaps
    //                 for (let orderIdx = 0; orderIdx < this.columns.length; orderIdx++) {
    //                     this.columns[orderIdx].order = orderIdx;
    //                 }
    //             }
    //         });
    //     }
    // }

    // public removeColumnsByTatTypeID(tatTypeID: number) {
    //     // Find the item
    //     const itemsToRemove = Enumerable
    //         .from(this.columns)
    //         .where((col: DRCXConfigurationSectionColumnModel) => col.tatTypeID === tatTypeID)
    //         .toArray();

    //     // Did we find any?
    //     if (itemsToRemove && itemsToRemove.length > 0) {
    //         itemsToRemove.forEach(itemToRemove => {
    //             const index = this.columns.indexOf(itemToRemove);
    //             if (index > -1) {
    //                 this.columns.splice(index, 1);

    //                 // Reset the column orders to remove gaps
    //                 for (let orderIdx = 0; orderIdx < this.columns.length; orderIdx++) {
    //                     this.columns[orderIdx].order = orderIdx;
    //                 }
    //             }
    //         });
    //     }
    // }

    // public removeColumnsByColumnList(itemsToRemove: Array<DRCXConfigurationSectionColumnModel>) {

    //     // Did we find any?
    //     if (itemsToRemove && itemsToRemove.length > 0) {
    //         itemsToRemove.forEach(itemToRemove => {
    //             const index = this.columns.indexOf(itemToRemove);
    //             if (index > -1) {
    //                 this.columns.splice(index, 1);

    //                 // Reset the column orders to remove gaps
    //                 for (let orderIdx = 0; orderIdx < this.columns.length; orderIdx++) {
    //                     this.columns[orderIdx].order = orderIdx;
    //                 }
    //             }
    //         });
    //     }
    // }

    // public clearColumns(): void {
    //     this.columns.length = 0;
    // }

    // public appendColumns(columns: Array<DRCXConfigurationSectionColumnModel>): void {
    //     // Add the columns to the end of the array and clean up the ordering
    //     this.columns.push(...columns);
    //     this.columns.forEach((column, index) => {
    //         column.order = index;
    //     });
    // }

    // public get optionalColumns(): Array<DRCXConfigurationSectionColumnModel> {
    //     return Enumerable
    //         .from(this.columns)
    //         .where(column => column.columnType === ColumnType.Optional)
    //         .toArray();
    // }

    public get tatColumns(): Array<DRCXConfigurationSectionColumnModel> {
        return Enumerable
            .from(this.columns)
            .where(column => column.columnType === ColumnType.Tat)
            .toArray();
    }

    // public get tatTypeColumns(): Array<DRCXConfigurationSectionColumnModel> {
    //     return Enumerable
    //         .from(this.columns)
    //         .where(column => column.columnType === ColumnType.TatType)
    //         .toArray();
    // }

    // public get eventColumns(): Array<DRCXConfigurationSectionColumnModel> {
    //     return Enumerable
    //         .from(this.columns)
    //         .where(column => column.columnType === ColumnType.Event)
    //         .toArray();
    // }

    public get hasObjects(): boolean {
        return this.objects && this.objects.length > 0;
    }

    public get hasDevices(): boolean {
        return (Enumerable.from(this.objects).any(o => o.objectType === eLynxObjectTypeEnum.Group &&
            o.targetObjectType === eLynxObjectTypeEnum.Device) ||
            Enumerable.from(this.objects).any(o => o.objectType === eLynxObjectTypeEnum.Device));
    }

    public get hasTanks(): boolean {
        return (Enumerable.from(this.objects).any(o => o.objectType === eLynxObjectTypeEnum.Group &&
            o.targetObjectType === eLynxObjectTypeEnum.Tank) ||
            Enumerable.from(this.objects).any(o => o.objectType === eLynxObjectTypeEnum.Tank));
    }

    public get hasWells(): boolean {
        return (Enumerable.from(this.objects).any(o => o.objectType === eLynxObjectTypeEnum.Group &&
            o.targetObjectType === eLynxObjectTypeEnum.Well) ||
            Enumerable.from(this.objects).any(o => o.objectType === eLynxObjectTypeEnum.Well));
    }

    // IEquatable Implementation
    public get equatableKey(): string {
        return this._equatableKey;
    }

    // UI Related
    // public expand() {
    //     this.isExpanded = true;
    // }

    // public collapse() {
    //     this.isExpanded = false;
    // }

    // public toggle() {
    //     this.isExpanded = !this.isExpanded;
    // }

    // public scrollTo() {
    //     this.scrollIntoView = true;
    // }

    protected _toDTO(dto: IDRCXConfigurationSectionDTOBase): IDRCXConfigurationSectionDTOBase {
        dto.id = this.id;
        dto.name = this.name;
        dto.order = this.order;
        dto.style = this.style;
        dto.componentName = this.componentName;
        dto.procedureName = this.procedureName;
        dto.created = this.created.toISOString();
        dto.dataSource = this.dataSource;
        dto.useTatEffectiveDate = this.useTatEffectiveDate;
        dto.varianceIgnoreEffectiveDayOffset = this.varianceIgnoreEffectiveDayOffset;
        dto.noAlarmDataForAggregates = this.noAlarmDataForAggregates;
        dto.includeDaysWithNoData = this.includeDaysWithNoData;
        dto.includeDevicesWithNoData = this.includeDevicesWithNoData;
        dto.includeDevicesWithNoTags = this.includeDevicesWithNoTags;
        dto.includeTanks = this.includeTanks;
        dto.includeTotals = this.includeTotals;
        dto.dateSortOrder = this.dateSortOrder;
        dto.deviceSortOrder = this.deviceSortOrder;
        dto.deviceSortColumn = this.deviceSortColumn;
        if (this.durationType === DurationTimeframeTypeEnum.MultipleTimeFrames) {
            dto.timeframe = {
                durationTimeframeType: this.durationType,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: this.multipleStartOffset as number,
                    rangeType: this.multipleStartRangeType as DateRangeType
                },
                duration: {
                    duration: this.multipleDuration as number,
                    rangeType: this.multipleDurationRangeType as DateRangeType
                },
                currentDateOffset: undefined
            };
        } else if (this.durationType === DurationTimeframeTypeEnum.CurrentSingleTimeFrame) {
            dto.timeframe = {
                durationTimeframeType: this.durationType,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: 0,
                    rangeType: this.currentRangeType as DateRangeType
                },
                duration: {
                    duration: 1,
                    rangeType: this.currentRangeType as DateRangeType
                },
                currentDateOffset: undefined
            };
        } else if (this.durationType === DurationTimeframeTypeEnum.PreviousSingleTimeFrame) {
            dto.timeframe = {
                durationTimeframeType: this.durationType,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: -1,
                    rangeType: this.previousRangeType as DateRangeType
                },
                duration: {
                    duration: 1,
                    rangeType: this.previousRangeType as DateRangeType
                },
                currentDateOffset: undefined
            };
        } else if (this.durationType === DurationTimeframeTypeEnum.SpecificStartOrStop) {
            dto.timeframe = {
                durationTimeframeType: this.durationType,
                fixedStartDate: {
                    timestamp: this.fixedStartDate?.toISOString() as string,
                    rangeType: this.fixedStartDateRangeType as DateRangeType
                },
                fixedEndDate: {
                    timestamp: this.fixedEndDate?.toISOString() as string,
                    rangeType: this.fixedEndDateRangeType as DateRangeType
                },
                runToCurrentDate: false,
                calculatedStartDateOffset: undefined,
                duration: undefined,
                currentDateOffset: undefined
            };
        } else {
            dto.timeframe = {
                durationTimeframeType: undefined,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: undefined,
                calculatedStartDateOffset: undefined,
                duration: undefined,
                currentDateOffset: undefined
            };
        }
        dto.retrievalStartOffset = this.retrievalStartOffset;
        dto.retrievalEndOffset = this.retrievalEndOffset;
        dto.consumerHandlesSorting = this.consumerHandlesSorting;
        dto.includeDeviceCommStatus = this.includeDeviceCommStatus;
        dto.includeTagLKVAlarmStatus = this.includeTagLKVAlarmStatus;
        dto.getAlarmData = this.getAlarmData;
        dto.includeTagLKVStaleDataStatus = this.includeTagLKVStaleDataStatus;
        dto.lockedColumnCount = this.lockedColumnCount;
        dto.checkAccessControl = this.checkAccessControl;
        dto.displayDeviceMemos = this.displayDeviceMemos;
        dto.displaySettingsChanges = this.displaySettingsChanges;
        dto.displayTagMemos = this.displayTagMemos;
        dto.rawDataClosenessMin = this.rawDataClosenessMin;
        dto.rawDataClosenessSec = this.rawDataClosenessSec;

        dto.objects = this.objects
            ? this.objects.map(object => object.toDTO())
            : undefined;
        dto.columns = this.columns
            ? this.columns.map(column => column.toDTO())
            : undefined;
        return dto;
    }
}
